import { getReplenishmentCalendar, getReplenishmentSku, type InstrumentRow, type RequirementRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { ActionIcon, Alert, Badge, Button, Group, Loader, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { ArrowLeft, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { formatCount } from '@utils/currency';
import { useCallback, useEffect, useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import ApprovalSheet from './ApprovalSheet';
import classes from './calendar.module.css';
import { addDaysKey, dayLabel, localDateKey, monthEndKey, monthStartKey, whenLabel } from './dates';
import PivotPanel, { type PivotKind } from './PivotPanel';
import SkuWindow from './SkuWindow';
import { StatusBadge } from './tables';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

type Unit = 'DAY' | 'WEEK' | 'MONTH';
type StatusFilter = 'PROPOSED' | 'APPROVED' | 'ALL';
export type Kind = 'PRODUCTION' | 'MATERIAL' | 'FG' | 'TRANSFER';
type KindFilter = Kind | 'ALL';
type Mode = 'CALENDAR' | 'TABLE';

const KIND_LABEL: Record<Kind, string> = { PRODUCTION: 'Production order', MATERIAL: 'Material PO', FG: 'FG purchase order', TRANSFER: 'Transfer' };
const KIND_SHORT: Record<Kind, string> = { PRODUCTION: 'Run', MATERIAL: 'PO', FG: 'FG PO', TRANSFER: 'STN' };
const KIND_ORDER: Kind[] = ['PRODUCTION', 'MATERIAL', 'FG', 'TRANSFER'];
const kindOf = (r: InstrumentRow): Kind => (r.type === 'WORK_ORDER' ? 'PRODUCTION' : r.type === 'MATERIAL_PO' ? 'MATERIAL' : r.type === 'FG_PO' ? 'FG' : 'TRANSFER');
const isApproved = (r: InstrumentRow): boolean => r.status !== 'PROPOSED';
const partyOf = (r: InstrumentRow): string => (r.type === 'TRANSFER' ? `${r.fromFacilityId} → ${r.toFacilityId}` : r.vendorName ?? r.vendorId ?? r.fromFacilityId ?? 'no vendor');

// ---- date helpers (yyyy-MM-dd keys, local time, Monday-first weeks)
const toDate = (key: string): Date => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const weekStartKey = (key: string): string => {
    const d = toDate(key);
    return addDaysKey(key, -((d.getDay() + 6) % 7));
};
const monthKey = (key: string): string => key.slice(0, 7);
const addMonthsKey = (key: string, months: number): string => {
    const d = toDate(monthStartKey(key));
    return localDateKey(new Date(d.getFullYear(), d.getMonth() + months, 1));
};
const monthTitle = (key: string): string => toDate(monthStartKey(key)).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
const shortDay = (key: string): string => toDate(key).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
const rangeLabel = (from: string, to: string): string => `${dayLabel(from)} – ${dayLabel(to)}`;

/** A cell of the calendar: a day, a week or a month, with the rows whose place-by falls in it. */
type Cell = { key: string; from: string; to: string; label: string; rows: InstrumentRow[] };
/** A pill: one kind, proposed or approved, for a party (day cells) or for the whole cell (week and month cells). */
type Pill = { key: string; kind: Kind; approved: boolean; text: string; count: number; rows: InstrumentRow[] };

const Pills: FC<{ pills: Pill[]; max: number; onOpen: (p: Pill) => void; onMore: () => void }> = ({ pills, max, onOpen, onMore }) => (
    <>
        {pills.slice(0, max).map((p) => (
            <Tooltip key={p.key} label={`${KIND_LABEL[p.kind]} · ${p.text} · ${p.count} line${p.count === 1 ? '' : 's'} · ${p.approved ? 'approved or later' : 'proposed'}`} withArrow openDelay={300}>
                <div
                    className={classes.pill}
                    data-kind={p.kind}
                    data-approved={p.approved ? 'true' : 'false'}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpen(p);
                    }}
                >
                    <span className={classes.pillText}>{p.text}</span>
                    <span className={classes.pillCount}>{p.count}</span>
                </div>
            </Tooltip>
        ))}
        {pills.length > max && (
            <span
                className={classes.more}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                    e.stopPropagation();
                    onMore();
                }}
            >
                +{pills.length - max} more
            </span>
        )}
    </>
);

const Lines2: FC<{ a: string; b: string; strong?: boolean; late?: boolean }> = ({ a, b, strong, late }) => (
    <Stack gap={0}>
        <Text size={strong ? 'sm' : 'xs'} fw={strong ? 700 : 500} c={late ? 'red' : undefined} truncate>
            {a}
        </Text>
        {b && (
            <Text size="10px" c="dimmed" truncate>
                {b}
            </Text>
        )}
    </Stack>
);

/** Everything on one cell, grouped by kind, each line with its status, document and why. */
const CellWindow: FC<{ title: string; subtitle: string; rows: InstrumentRow[]; today: string; onClose: () => void; onSku: (productId: string) => void; onApprove: (ids: string[]) => void }> = ({ title, subtitle, rows, today, onClose, onSku, onApprove }) => {
    const columns = useMemo<DataSheetColumn<InstrumentRow>[]>(
        () => [
            { key: 'item', header: 'Line', width: 240, sticky: true, sortable: true, valueAccessor: (r) => r.itemName ?? r.itemId, render: (_v, r): ReactElement => <Lines2 a={r.itemName ?? r.itemId} b={`${r.sku}${r.unit ? ` · ${r.unit}` : ''}`} strong /> },
            { key: 'qty', header: 'Qty', width: 90, align: 'right', sortable: true, valueAccessor: (r) => r.qty, render: (_v, r): ReactElement => <Text size="sm" fw={700}>{n(r.qty, r.unit ? 1 : 0)}</Text> },
            { key: 'party', header: 'Vendor · factory · lane', width: 200, sortable: true, valueAccessor: partyOf, render: (_v, r): ReactElement => <Lines2 a={partyOf(r)} b={r.leadDays != null ? `${r.leadDays}d lead` : ''} /> },
            { key: 'dates', header: 'Place by · lands', width: 150, sortable: true, valueAccessor: (r) => r.placeBy, render: (_v, r): ReactElement => <Lines2 a={whenLabel(r.placeBy, today)} b={`lands ${dayLabel(r.expectedLandDate ?? r.landDate)}`} late={r.placeBy <= today && r.status === 'PROPOSED'} /> },
            {
                key: 'status',
                header: 'Status · document',
                width: 170,
                sortable: true,
                valueAccessor: (r) => r.status,
                render: (_v, r): ReactElement => (
                    <Group gap={6} wrap="nowrap">
                        <StatusBadge status={r.status} />
                        {r.documentCode && (
                            <Badge size="xs" variant="outline" tt="none">
                                {r.documentCode}
                            </Badge>
                        )}
                    </Group>
                ),
            },
            {
                key: 'why',
                header: 'Why',
                width: 320,
                render: (_v, r): ReactElement => {
                    if (r.status === 'PROPOSED') {
                        const w = r.requirement;
                        const run = r.feeds[0];
                        const more = r.feeds.length - 1;
                        return <Lines2 a={w ? `${r.sku} dips ${whenLabel(w.dipDate, today)} · zero ${dayLabel(w.zeroDate)}${w.stockoutDays > 0 ? ` · ${w.stockoutDays}d at zero` : ''}` : `for ${r.sku}`} b={run ? `feeds ${run.type === 'FG_PO' ? 'order' : 'run'} ${run.sku} ${dayLabel(run.placeBy)}${more > 0 ? ` +${more} more` : ''} · ${run.status === 'PROPOSED' ? 'run not approved yet' : `run ${run.status.toLowerCase()}${run.documentCode ? ` · ${run.documentCode}` : ''}`}` : `lands ${dayLabel(r.landDate)} · from the latest run`} late={Boolean(w?.dipDate && w.dipDate <= today)} />;
                    }
                    const s = (r.requirementSnapshot ?? {}) as { dipDate?: string | null; zeroDate?: string | null; requiredQty?: number; landDate?: string | null; runDate?: string | null };
                    return <Lines2 a={`approved ${r.approvedAt ? dayLabel(r.approvedAt.slice(0, 10)) : '—'} for the dip on ${dayLabel(s.dipDate)}${s.zeroDate ? ` · zero ${dayLabel(s.zeroDate)}` : ''}`} b={`${s.requiredQty != null ? `needed ${n(s.requiredQty)} · ` : ''}planned to land ${dayLabel(s.landDate ?? r.landDate)}${s.runDate ? ` · from run of ${dayLabel(s.runDate)}` : ''}`} />;
                },
            },
        ],
        [today],
    );
    const groups = KIND_ORDER.map((k) => ({ kind: k, rows: rows.filter((r) => kindOf(r) === k) })).filter((g) => g.rows.length > 0);
    const proposed = rows.filter((r) => r.status === 'PROPOSED');
    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            footer={
                proposed.length > 0 ? (
                    <Group justify="space-between" p="sm">
                        <Text size="xs" c="dimmed">
                            A proposed line opens the review sheet with its whole chain. Click a line to see the SKU's curve.
                        </Text>
                        <Button size="xs" onClick={() => onApprove(proposed.map((r) => r.id))}>
                            Review &amp; approve {proposed.length} proposed
                        </Button>
                    </Group>
                ) : undefined
            }
        >
            <Stack gap="md" p="md">
                {groups.map((g) => (
                    <Stack key={g.kind} gap={6}>
                        <div className={classes.kindHeader}>
                            <span className={classes.swatch} data-kind={g.kind} />
                            {KIND_LABEL[g.kind]}
                            <Text size="xs" c="dimmed" fw={400}>
                                {g.rows.length} line{g.rows.length === 1 ? '' : 's'} · {new Set(g.rows.map(partyOf)).size} {g.kind === 'PRODUCTION' ? 'factories' : g.kind === 'TRANSFER' ? 'lanes' : 'vendors'}
                            </Text>
                        </div>
                        <DataSheet<InstrumentRow> data={g.rows} columns={columns} getRowKey={(r) => r.id} rowHeight={48} bare hideHeaderBar onRowClick={(r) => onSku(r.productId)} />
                    </Stack>
                ))}
            </Stack>
        </ExplainWindow>
    );
};

/**
 * The plan on a calendar. Each instrument sits on its place-by day: the day a run starts,
 * an order is placed, a transfer leaves. Proposed rows exist from today to the run's horizon
 * and are rewritten every run; approved rows stay where they were placed, so the past shows
 * only those. Day cells carry a pill per party, week and month cells a pill per kind.
 */
const CalendarView: FC<{ kind?: KindFilter; from?: string; to?: string; mode?: Mode; onBack?: () => void }> = ({ kind: initialKind = 'ALL', from: windowFrom, to: windowTo, mode: initialMode = 'CALENDAR', onBack }) => {
    const today = localDateKey();
    const [unit, setUnit] = useState<Unit>('DAY');
    const [status, setStatus] = useState<StatusFilter>('ALL');
    const [kind, setKind] = useState<KindFilter>(initialKind);
    const [mode, setMode] = useState<Mode>(initialMode);
    const [anchor, setAnchor] = useState<string>(windowFrom ?? today);
    useEffect(() => setKind(initialKind), [initialKind]);
    useEffect(() => setMode(initialMode), [initialMode]);
    const [rows, setRows] = useState<InstrumentRow[]>([]);
    const [horizonEnd, setHorizonEnd] = useState<string | null>(null);
    const [runDate, setRunDate] = useState<string>(today);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState<{ title: string; subtitle: string; rows: InstrumentRow[] } | null>(null);
    const [skuDetail, setSkuDetail] = useState<RequirementRow[] | null>(null);
    const [approvalIds, setApprovalIds] = useState<string[] | null>(null);

    // The visible range per unit: a month of days, a quarter of weeks, a year of months.
    const range = useMemo((): { from: string; to: string; title: string } => {
        if (unit === 'DAY') {
            const from = weekStartKey(monthStartKey(anchor));
            return { from, to: addDaysKey(from, 41), title: monthTitle(anchor) };
        }
        if (unit === 'WEEK') {
            const from = weekStartKey(anchor);
            const to = addDaysKey(from, 13 * 7 - 1);
            return { from, to, title: `13 weeks · ${rangeLabel(from, to)}` };
        }
        const from = `${anchor.slice(0, 4)}-01-01`;
        return { from, to: `${anchor.slice(0, 4)}-12-31`, title: anchor.slice(0, 4) };
    }, [unit, anchor]);

    // The window a card asked for (next N days) can reach past the visible month: load both.
    const loadFrom = windowFrom && windowFrom < range.from ? windowFrom : range.from;
    const loadTo = windowTo && windowTo > range.to ? windowTo : range.to;
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getReplenishmentCalendar(loadFrom, loadTo, 'ALL');
            setRows(res.rows);
            setHorizonEnd(res.horizonEnd);
            setRunDate(res.runDate);
        } catch (e) {
            const err = e as { error?: { message?: string }; message?: string };
            setError(err?.error?.message ?? err?.message ?? 'Could not load the calendar');
        } finally {
            setLoading(false);
        }
    }, [loadFrom, loadTo]);
    useEffect(() => {
        void load();
    }, [load]);

    const visible = useMemo(() => rows.filter((r) => (status === 'ALL' ? true : status === 'PROPOSED' ? !isApproved(r) : isApproved(r))).filter((r) => kind === 'ALL' || kindOf(r) === kind), [rows, status, kind]);
    // Table mode lists the card's window when one was given, else what the calendar shows.
    const tableRows = useMemo(() => (windowFrom && windowTo ? visible.filter((r) => r.placeBy >= windowFrom && r.placeBy <= windowTo) : visible.filter((r) => r.placeBy >= range.from && r.placeBy <= range.to)), [visible, windowFrom, windowTo, range.from, range.to]);
    const tableTitle = windowFrom && windowTo ? `${rangeLabel(windowFrom, windowTo)}` : range.title;
    const byDay = useMemo(() => {
        const m = new Map<string, InstrumentRow[]>();
        for (const r of visible) m.set(r.placeBy, [...(m.get(r.placeBy) ?? []), r]);
        return m;
    }, [visible]);

    const step = (dir: 1 | -1): void => {
        if (unit === 'DAY') setAnchor(addMonthsKey(anchor, dir));
        else if (unit === 'WEEK') setAnchor(addDaysKey(weekStartKey(anchor), dir * 13 * 7));
        else setAnchor(`${Number(anchor.slice(0, 4)) + dir}-01-01`);
    };

    const cells = useMemo((): Cell[] => {
        const out: Cell[] = [];
        if (unit === 'DAY') {
            for (let i = 0; i < 42; i += 1) {
                const key = addDaysKey(range.from, i);
                out.push({ key, from: key, to: key, label: shortDay(key), rows: byDay.get(key) ?? [] });
            }
        } else if (unit === 'WEEK') {
            for (let w = 0; w < 13; w += 1) {
                const from = addDaysKey(range.from, w * 7);
                const to = addDaysKey(from, 6);
                const list: InstrumentRow[] = [];
                for (let i = 0; i < 7; i += 1) list.push(...(byDay.get(addDaysKey(from, i)) ?? []));
                out.push({ key: from, from, to, label: `Week of ${dayLabel(from)}`, rows: list });
            }
        } else {
            for (let m = 0; m < 12; m += 1) {
                const from = addMonthsKey(range.from, m);
                const to = monthEndKey(from);
                out.push({ key: from, from, to, label: monthTitle(from), rows: visible.filter((r) => r.placeBy >= from && r.placeBy <= to) });
            }
        }
        return out;
    }, [unit, range.from, byDay, visible]);

    const pillsOf = (cell: Cell): Pill[] => {
        const m = new Map<string, Pill>();
        for (const r of cell.rows) {
            const k = kindOf(r);
            const approved = isApproved(r);
            const party = unit === 'DAY' ? partyOf(r) : '';
            const key = `${k}|${approved}|${party}`;
            const cur = m.get(key) ?? { key, kind: k, approved, text: unit === 'DAY' ? `${KIND_SHORT[k]} · ${party}` : KIND_LABEL[k], count: 0, rows: [] };
            cur.count += 1;
            cur.rows.push(r);
            m.set(key, cur);
        }
        const list = [...m.values()].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || Number(b.approved) - Number(a.approved) || b.count - a.count);
        if (unit !== 'DAY') {
            for (const p of list) {
                const parties = new Set(p.rows.map(partyOf)).size;
                const units = p.kind === 'PRODUCTION' ? ` · ${n(p.rows.reduce((s, r) => s + r.qty, 0))} units` : '';
                p.text = `${KIND_LABEL[p.kind]}${p.approved ? ' · approved' : ''} · ${parties} ${p.kind === 'PRODUCTION' ? (parties === 1 ? 'factory' : 'factories') : p.kind === 'TRANSFER' ? (parties === 1 ? 'lane' : 'lanes') : parties === 1 ? 'vendor' : 'vendors'}${units}`;
            }
        }
        return list;
    };

    const openCell = (cell: Cell, subset?: Pill): void => {
        const list = subset ? subset.rows : cell.rows;
        const proposed = list.filter((r) => !isApproved(r)).length;
        setOpen({
            title: unit === 'DAY' ? cell.label : cell.label + (unit === 'WEEK' ? ` · ${rangeLabel(cell.from, cell.to)}` : ''),
            subtitle: `${subset ? `${KIND_LABEL[subset.kind]} · ${subset.text} · ` : ''}${list.length} line${list.length === 1 ? '' : 's'} · ${proposed} proposed · ${list.length - proposed} approved or later`,
            rows: list,
        });
    };

    const legend = (
        <div className={classes.legend}>
            {KIND_ORDER.map((k) => (
                <span key={k} className={classes.legendItem}>
                    <span className={classes.swatch} data-kind={k} />
                    {KIND_LABEL[k]}
                </span>
            ))}
            <span className={classes.legendItem}>
                <span className={classes.swatch} data-proposed="true" />
                light = proposed · solid = approved or placed
            </span>
            <span className={classes.legendItem}>
                <span className={classes.swatch} data-beyond="true" />
                beyond the run's horizon{horizonEnd ? ` (${dayLabel(horizonEnd)})` : ''}: nothing planned yet
            </span>
            <span className={classes.legendItem}>
                <Text size="xs" c="dimmed">
                    before today only approved rows remain · as of run {dayLabel(runDate)}
                </Text>
            </span>
        </div>
    );

    const tablePanels = (): ReactElement => {
        const kinds: Kind[] = kind === 'ALL' ? KIND_ORDER.filter((k) => tableRows.some((r) => kindOf(r) === k)) : [kind];
        if (kinds.length === 0) {
            return (
                <Text size="sm" c="dimmed">
                    Nothing to place in {tableTitle}.
                </Text>
            );
        }
        const asPivot = (k: Kind): PivotKind => k;
        return (
            <Stack gap="lg">
                {kinds.map((k) => (
                    <PivotPanel key={k} kind={asPivot(k)} rows={tableRows.filter((r) => kindOf(r) === k)} skus={[]} title={KIND_LABEL[k] + 's'} subtitle={`place by ${tableTitle}`} onOpenSku={(productId) => void getReplenishmentSku(productId).then(setSkuDetail).catch(() => setSkuDetail(null))} onApprove={(ids) => setApprovalIds(ids)} onChanged={() => void load()} />
                ))}
            </Stack>
        );
    };

    return (
        <Stack gap="sm" px="md" pb="xl">
            <Group className={classes.toolbar} justify="space-between" align="center">
                <Group gap="xs" align="center">
                    {onBack && (
                        <Button size="xs" variant="default" leftSection={<ArrowLeft size={14} />} onClick={onBack}>
                            Plan
                        </Button>
                    )}
                    {mode === 'CALENDAR' && (
                        <>
                            <ActionIcon variant="default" size="md" onClick={() => step(-1)} aria-label="Previous">
                                <CaretLeft size={14} />
                            </ActionIcon>
                            <ActionIcon variant="default" size="md" onClick={() => step(1)} aria-label="Next">
                                <CaretRight size={14} />
                            </ActionIcon>
                            <Button size="xs" variant="default" onClick={() => setAnchor(today)}>
                                Today
                            </Button>
                        </>
                    )}
                    <span className={classes.title}>{mode === 'CALENDAR' ? range.title : tableTitle}</span>
                    {loading && <Loader size={14} />}
                </Group>
                <Group gap="sm" align="center">
                    <SegmentedControl size="xs" value={kind} onChange={(v) => setKind(v as KindFilter)} data={[{ value: 'ALL', label: 'All' }, { value: 'PRODUCTION', label: 'Runs' }, { value: 'MATERIAL', label: 'RM / PM POs' }, { value: 'FG', label: 'FG POs' }, { value: 'TRANSFER', label: 'STNs' }]} />
                    <SegmentedControl size="xs" value={status} onChange={(v) => setStatus(v as StatusFilter)} data={[{ value: 'ALL', label: 'Both' }, { value: 'PROPOSED', label: 'Proposed' }, { value: 'APPROVED', label: 'Approved' }]} />
                    {mode === 'CALENDAR' && <SegmentedControl size="xs" value={unit} onChange={(v) => setUnit(v as Unit)} data={[{ value: 'DAY', label: 'Day' }, { value: 'WEEK', label: 'Week' }, { value: 'MONTH', label: 'Month' }]} />}
                    <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as Mode)} data={[{ value: 'CALENDAR', label: 'Calendar' }, { value: 'TABLE', label: 'Table' }]} />
                </Group>
            </Group>

            {error && (
                <Alert color="red" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {mode === 'TABLE' && tablePanels()}

            {mode === 'CALENDAR' && unit === 'DAY' && (
                <div className={classes.monthGrid}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                        <div key={d} className={classes.weekday}>
                            {d}
                        </div>
                    ))}
                    {cells.map((cell) => {
                        const outside = monthKey(cell.key) !== monthKey(anchor);
                        const beyond = horizonEnd != null && cell.key > horizonEnd;
                        const pills = pillsOf(cell);
                        return (
                            <div key={cell.key} className={classes.cell} data-outside={outside ? 'true' : undefined} data-past={cell.key < today ? 'true' : undefined} data-today={cell.key === today ? 'true' : undefined} data-beyond={beyond ? 'true' : undefined} role="button" tabIndex={0} onClick={() => cell.rows.length && openCell(cell)}>
                                <div className={classes.dayNumber}>
                                    <span>{toDate(cell.key).getDate()}</span>
                                    {cell.key === today && <span className={classes.cellHint}>today</span>}
                                    {cell.rows.length > 0 && <span className={classes.cellHint}>{cell.rows.length}</span>}
                                </div>
                                <Pills pills={pills} max={4} onOpen={(p) => openCell(cell, p)} onMore={() => openCell(cell)} />
                            </div>
                        );
                    })}
                </div>
            )}

            {mode === 'CALENDAR' && unit === 'WEEK' && (
                <div className={classes.weekList}>
                    {cells.map((cell) => {
                        const current = today >= cell.from && today <= cell.to;
                        const pills = pillsOf(cell);
                        return (
                            <div key={cell.key} className={classes.weekRow} data-current={current ? 'true' : undefined} data-past={cell.to < today ? 'true' : undefined} role="button" tabIndex={0} onClick={() => cell.rows.length && openCell(cell)}>
                                <div>
                                    <div className={classes.weekLabel}>{rangeLabel(cell.from, cell.to)}</div>
                                    <div className={classes.weekSub}>
                                        {cell.rows.length === 0 ? (horizonEnd && cell.from > horizonEnd ? 'beyond the horizon' : 'nothing to place') : `${cell.rows.length} line${cell.rows.length === 1 ? '' : 's'}`}
                                        {current ? ' · this week' : ''}
                                    </div>
                                </div>
                                <div className={classes.pillRow}>
                                    <Pills pills={pills} max={8} onOpen={(p) => openCell(cell, p)} onMore={() => openCell(cell)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {mode === 'CALENDAR' && unit === 'MONTH' && (
                <div className={classes.yearGrid}>
                    {cells.map((cell) => {
                        const current = monthKey(cell.key) === monthKey(today);
                        const pills = pillsOf(cell);
                        return (
                            <div key={cell.key} className={classes.monthCard} data-current={current ? 'true' : undefined} data-past={cell.to < today ? 'true' : undefined} role="button" tabIndex={0} onClick={() => cell.rows.length && openCell(cell)}>
                                <Text size="sm" fw={700}>
                                    {cell.label}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {cell.rows.length === 0 ? (horizonEnd && cell.from > horizonEnd ? 'beyond the horizon' : 'nothing to place') : `${cell.rows.length} line${cell.rows.length === 1 ? '' : 's'} · ${new Set(cell.rows.map(partyOf)).size} parties`}
                                </Text>
                                <div className={classes.pillRow}>
                                    <Pills pills={pills} max={8} onOpen={(p) => openCell(cell, p)} onMore={() => openCell(cell)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {mode === 'CALENDAR' && legend}

            {open && (
                <CellWindow
                    title={open.title}
                    subtitle={open.subtitle}
                    rows={open.rows}
                    today={today}
                    onClose={() => setOpen(null)}
                    onSku={async (productId) => setSkuDetail(await getReplenishmentSku(productId).catch(() => null))}
                    onApprove={(ids) => setApprovalIds(ids)}
                />
            )}
            <SkuWindow rows={skuDetail} onClose={() => setSkuDetail(null)} onApprove={(ids) => setApprovalIds(ids)} />
            <ApprovalSheet
                ids={approvalIds}
                onClose={() => setApprovalIds(null)}
                onDone={() => {
                    setOpen(null);
                    void load();
                }}
            />
        </Stack>
    );
};

export default CalendarView;
